/**
 * @module Experince/Shop/Order-Confirmation
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
import { Injectable } from '@angular/core';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// service
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';

// api endpoints
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// Interface
import { APIError } from '@support/api/common/error.interface';
import { OrderSummaryInterface } from '@support/api/proxies/responses/api-2814-new.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';
import { ShoppingCart } from '@experience/shop/cart/models/cart.model';
import {
  CustomerDetailsResponse,
  CustomerDetails,
} from '@support/api/proxies/responses/api-2007.interface';
import { UserService } from '@support/shared/services/user.service';
import { VIEW_STATEMENT } from '@experience/shop/order-confirmation/constants/order-confirmation.constants';
import { HttpResponse } from '@angular/common/http';

@Injectable()
export class OrderConfirmationService {
  /**
   * native window object
   */
  public windowContainer: Window;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private userService: UserService,
  ) {}
  /**
   * Assign displayThankYou
   * @returns void
   */
  public displayThankYou(): void {
    // Ingenico services not requiured for WEB
    /** if (this.stateStoreUtilsService.isTapestry()) {
      this.infineaService.displayIpcMessage('Thank you');
    } else {
      this.ingenicoService.displayThankYou('E');
    }
    */
  }

  /**
   * Assign displayAds
   * @returns void
   */
  public displayAds(): void {
    /** Ingenico Service not required for Web 
    if (this.stateStoreUtilsService.isTapestry()) {
      this.infineaService.resetIpc();
    } else {
      this.ingenicoService.displayAds();
    }
    */
  }

  /**
   * Get OrderDetails
   * @return Observable<Order>
   */
  public getOrderDetails(orderId?: string): Observable<OrderSummaryInterface> {
    // const isRap = (SETTINGS.CHANNEL.toLocaleLowerCase() === 'nrd');
    const cartId = localStorage.getItem('cartId');
    // orderId: orderId ? orderId : isRap ? cartId : this.stateStoreUtilsService.getCartId()
    const pathParams = {
      orderId: orderId ? orderId : this.stateStoreUtilsService.getCartId(),
    };
    const payload = {
      accountId: this.stateStoreUtilsService.getAccountId() || '',
      sendOrderDetailsOnly: true,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2814_getOrderDetails, payload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const orderDetails = response.body;
          this.stateStoreUtilsService.setOrderSummaryData(orderDetails);
          const emptyCart: Cart = new ShoppingCart();
          this.stateStoreUtilsService.deleteCart();
          this.cartSupportService.updatePDLCartObjectAndBlockNUpdates(orderDetails, 1);
          this.cartSupportService.announceCartUpdate(emptyCart);
          return orderDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Get OrderDetails
   * @return Observable<Order>
   */
  public getOrderSubmissionDetails(orderId?: string): Observable<any> {
    const payload = [
      {
        orderId: orderId ? orderId : this.stateStoreUtilsService.getNewCartId(),
      },
    ];
    return this.apiGatewayService
      .postRequest(API_MAP.API_2824_retrieveOrderStatus, payload, null, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param documentData: document
   * @returns documentUrl: string
   */
  public getDocument(documentId: string, orderId?: string, docType?: string): Observable<string> {
    // const isWebChannel = (SETTINGS.CHANNEL.toLocaleLowerCase() === VIEW_STATEMENT.WEB);
    let request = {};
    request = {
      // docReturnType: (isWebChannel) ? VIEW_STATEMENT.CONTENT : VIEW_STATEMENT.URL,
      orderAttachment: {
        docReturnType: VIEW_STATEMENT.CONTENT,
        orderId: orderId,
        documentId: documentId,
        documentType:
          docType.toLocaleUpperCase() === VIEW_STATEMENT.RECEIPT
            ? VIEW_STATEMENT.RECEIPT
            : docType === VIEW_STATEMENT.SHIPPING_LABEL
              ? undefined
              : VIEW_STATEMENT.TRANSACTION_CONFIRMATION,
      },
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_497_retrieveDocuments, request, null, null, null)
      .pipe(
        map((response: HttpResponse<Document>) => {
          // return this.getDocumentContent(response.body, isWebChannel);
          return this.getDocumentContent(response.body);
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param documentData: document
   * @param isWebChannel: boolean
   * @returns documentUrl: string
   */
  // private getDocumentContent(documentData: Document, isWebChannel: boolean): string {
  private getDocumentContent(documentData: Document): string {
    // if (isWebChannel) {
    return this.objectService.get(documentData, 'documentDetails.content.embeddedData');
    // }else {
    //   return this.objectService.get(documentData, 'documentDetails.documentURL');
    // }
  }

  /**
   * To open the PDF document in new window
   * @param url
   */
  public openWindow(url: string): void {
    const params = `scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no`;
    if (this.stateStoreUtilsService.isTapestry()) {
      const openPdfLink = document.createElement('a');
      openPdfLink.href = 'openpdf://url=' + url;
      openPdfLink.click();
    } else {
      window.open(url, '_blank', params);
    }
  }

  /**
   * Get customer details
   * @return Observable<CustomerDetails>
   */
  public getCustomerDetails(): Observable<CustomerDetails> {
    return this.userService.getCustomerDetailSearch().pipe(
      map((customerDetail: CustomerDetailsResponse) => {
        return this.objectService.get(customerDetail, 'customer');
      })
    );
  }

  /**
   * Clear customer information from cache
   */
  public clearCustomerData(isAuthenticated: boolean): void {
    this.cartSupportService.clearCustomerData(isAuthenticated);
  }
}
